import { Sync } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Grid,
  Slide,
  Typography,
  useTheme,
} from "@mui/material";
import {
  useGetInvitedContactsQuery,
  useResendInviteMutation,
} from "api/api-it";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import { DEFAULT_ROWS } from "codes";
import DataTablev2 from "components/Common/DataTablev2";
import NoData from "components/Common/NoData";
import SkeletonLoad from "components/Common/SkeletonLoad";
import useWidth from "helpers/useWidth";
import useIsMobile from "hooks/useIsMobile";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { clearAll } from "redux/reducers/searchReducer";
import InviteStatusIndication from "./InviteStatusIndication";

interface InvitedContact {
  contact_id: string;
  contact_name: string;
  invite_token: string;
  status: string;
  invited_by: string;
  invited_at: string;
  invite_accepted_at: string;
  contact_type: "internal" | "external" | "Investor" | "Sales_Partner";
}

interface ActionValue {
  contact_id: string;
  status: string;
  type: string;
}

const InvitedUsers: React.FC = () => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const [userType, setUserType] = useState("Internal");
  const ref = useRef<HTMLDivElement>(null);
  const width = ref ? useWidth(ref) : 1000;
  const searchValue = useAppSelector((state) => state.search.value);
  const dispatch = useAppDispatch();

  const { data: curr_user } = useGetCurrentUserQuery();
  const settings = curr_user?.settings ?? {};
  const rowsPerPage = settings?.rowsPerPage ?? DEFAULT_ROWS;

  const [queryParams, setQueryParams] = useState({
    per_page: 10, // Hardcoded to 10 initially
    page: 1,
    search: searchValue || undefined,
  });
  const [filters, setFilters] = useState<Record<string, any>>({});
  const hasSearchedOnce = useRef(false);

  const inviteduserparams = `?${new URLSearchParams(
    Object.fromEntries(
      Object.entries(queryParams)
        .filter(([_, value]) => value !== undefined) // Remove undefined values
        .map(([key, value]) => [key, String(value)]) // Convert all values to strings
    )
  ).toString()}${Object.entries(filters)
    .map(([key, value]) => {
      if (value?.keywords) {
        const apiValue = typeof value.keywords === "boolean"
          ? value.keywords.toString()
          : value.keywords;
        return `&${key}=${apiValue}`;
      }
      return "";
    })
    .filter(Boolean)
    .join("")}`;

  const {
    data: invitedUsersData,
    isFetching,
    refetch,
  } = useGetInvitedContactsQuery({ inviteduserparams });

  const [resendInvite] = useResendInviteMutation();

  useEffect(() => {
    dispatch(clearAll());
  }, [dispatch, userType]);

  useEffect(() => {
    if (searchValue && !hasSearchedOnce.current) {
      hasSearchedOnce.current = true;
    }
    if (hasSearchedOnce.current) {
      setQueryParams((prev) => ({ ...prev, page: 1, search: searchValue || undefined }));
    }
  }, [searchValue, userType]);

  const handleClearFilters = () => {
    setQueryParams({
      per_page: rowsPerPage,
      page: 1,
      search: undefined,
    });
    setFilters({});
    dispatch(clearAll());
    refetch();
  };

  const handleFilterSubmit = (filtersString: string) => {
    try {
      const filtersObj = JSON.parse(filtersString);
      setFilters(filtersObj);
      const newFilters: Record<string, string> = {};
      for (const key in filtersObj) {
        if (filtersObj[key]?.keywords) {
          if (key === "invited_at" && typeof filtersObj[key].keywords === "string") {
            newFilters[key] = filtersObj[key].keywords;
          } else if (Array.isArray(filtersObj[key].keywords)) {
            newFilters[key] = filtersObj[key].keywords.join(",");
          }
        }
      }
      setQueryParams((prev) => ({ ...prev, ...newFilters, page: 1 }));
    } catch (error) {
      console.error("Error parsing filters:", error);
    }
  };

  const handlePageChange = (newPage: number, newPerPage: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: newPage,
      per_page: newPerPage,
    }));
  };

  const formatInvitedUsersForTable = (users: InvitedContact[] = []) => {
    return users.map((item) => ({
      ...item,
      action: {
        contact_id: item.contact_id,
        status: item.status,
        type: item.contact_type,
      } as ActionValue,
    }));
  };

  const formattedData = formatInvitedUsersForTable(invitedUsersData?.invited_contacts);
  const headerLength = Object.keys(formattedData?.[0] ?? {}).length;
  const defaultColumnWidth = width ? (width / headerLength) * 1.6 : 200;
  const TO_BE_HIDDEN = ["invite_token", "contact_id"];

  const textSx = {
    fontSize: "1.1em!important",
    lineHeight: "1.5em",
    height: "auto",
    overflow: "hidden",
    maxWidth: "100%",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
    wordWrap: "break-word",
    minWidth: isMobile ? "100%" : "10vw",
  };

  const handleResendInvite = async (value: ActionValue) => {
    try {
      await resendInvite({
        contact_id: value.contact_id,
        type: value.type as "internal" | "external" | "Invistor" | "Sales_Partner",
      }).unwrap();
      toast.success("Invite resent successfully");
    } catch (err) {
      toast.error(`An error occurred: ${(err as Error).message}`);
    }
  };

  const customColumns = {
    contact_name: {
      contact_name: "Contact Name",
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string }) => (
        <Typography sx={textSx}>{value}</Typography>
      ),
    },
    contact_type: {
      contact_type: "Type",
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string }) => (
        <Typography sx={textSx}>{value}</Typography>
      ),
    },
    status: {
      status: "Status",
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string }) => (
        <InviteStatusIndication status={value} sx={{ textAlign: "center" }} />
      ),
    },
    invited_by: {
      invited_by: "Invited By",
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string }) => (
        <Typography sx={textSx}>{value}</Typography>
      ),
    },
    invited_at: {
      invited_at: "Invited At",
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string }) => (
        <Typography sx={textSx}>
          {moment(value).format("DD/MM/YYYY HH:mm")}
        </Typography>
      ),
    },
    invite_accepted_at: {
      invite_accepted_at: "Invite Accepted At",
      type: "text",
      flexGrow: 1,
      renderCell: ({ value }: { value: string }) => (
        <Typography sx={{ ...textSx, color: value ? "green" : "grey" }}>
          {value ? moment(value).format("DD/MM/YYYY HH:mm") : "None"}
        </Typography>
      ),
    },
    action: {
      action: "Action",
      type: "action",
      flexGrow: 1,
      renderCell: ({ value }: { value: ActionValue }) => (
        <LoadingButton
          variant="contained"
          disabled={value?.status === "ACCEPTED"}
          endIcon={<Sync />}
          onClick={() => handleResendInvite(value)}
          loading={isFetching}
          sx={{
            minWidth: isMobile ? "100%" : "120px",
            fontSize: isMobile ? "0.9em" : "1em",
          }}
        >
          Resend
        </LoadingButton>
      ),
    },
  };

  return (
    <Slide in direction="left">
      <Box sx={{ flexGrow: 1, p: 0 }}>
        <Grid item xs={12} ref={ref} sx={{ margin: "5px" }}>
          <Grid container spacing={1}>
            {isFetching && <SkeletonLoad bars={10} />}
            {!isFetching && formattedData && formattedData.length > 0 ? (
              <DataTablev2 
                disableFilters={false}
                clearFilters={handleClearFilters}
                onFilterSubmit={handleFilterSubmit}
                filterColumnTableName={invitedUsersData}
                pagination={invitedUsersData?.pagination}
                onPaginationChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                defaultColumnWidth={defaultColumnWidth}
                data={formattedData}
                toBeHidden={TO_BE_HIDDEN}
                filters={filters}
                setFilters={setFilters}
                customColumns={customColumns}
              />
            ) : (
              !isFetching && <NoData text="No users found" sx={{ margin: "auto" }} />
            )}
          </Grid>
        </Grid>
      </Box>
    </Slide>
  );
};

export default InvitedUsers;
